import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import { getAnalyticsStore, subscribeAnalytics, exportAnalyticsCSV, clearAnalytics, type OnboardingSession } from "../../lib/state";
import { getSessions, sendReminders } from "../../lib/api";
import { logAudit } from "../lib/audit";
import { pathColor, formatDateTime, formatDuration } from "../lib/format";

type Session = OnboardingSession;

const PATH_LABELS_AR: Record<string, string> = {
  finance: "المالية", procurement: "المشتريات", "project-management": "المشاريع", "assets-management": "الأصول", "hr-admin": "الموارد البشرية", commercial: "التجاري", corporate: "الاستراتيجية", development: "التقنية", "(none)": "بدون مسار",
};
const PATH_LABELS_EN: Record<string, string> = {
  finance: "Finance", procurement: "Procurement", "project-management": "Projects", "assets-management": "Assets", "hr-admin": "HR", commercial: "Commercial", corporate: "Corporate", development: "IT", "(none)": "No path yet",
};

// --- Completions bar chart (last 14 days) ---
function CompletionsChart({ sessions, lang }: { sessions: Session[]; lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const days = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const arr: { date: Date; count: number }[] = [];
    for (let i = 13; i >= 0; i--) arr.push({ date: new Date(today.getTime() - i * 86400000), count: 0 });
    sessions.forEach((s) => {
      if (!s.completedAt) return;
      const d = new Date(s.completedAt);
      d.setHours(0, 0, 0, 0);
      const idx = Math.round((d.getTime() - arr[0].date.getTime()) / 86400000);
      if (idx >= 0 && idx < 14) arr[idx].count++;
    });
    return arr;
  }, [sessions]);
  const max = Math.max(1, ...days.map((d) => d.count));
  const W = 720, H = 220, pad = 32;
  const barW = ((W - 2 * pad) / 14) * 0.7;
  const step = (W - 2 * pad) / 14;
  return (
    <div className="kd-chart-card">
      <h3 className="kd-chart-title">{t("إكمال الرحلة (آخر 14 يوماً)", "Journey Completions (last 14 days)")}</h3>
      <svg viewBox={`0 0 ${W} ${H}`} className="kd-chart-svg" role="img" aria-label="Completions per day">
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={pad} x2={W - pad} y1={H - pad - f * (H - 2 * pad)} y2={H - pad - f * (H - 2 * pad)} stroke="currentColor" strokeOpacity="0.08" />
            <text x={pad - 6} y={H - pad - f * (H - 2 * pad) + 4} textAnchor="end" fontSize="10" fill="currentColor" opacity="0.6">{Math.round(max * f)}</text>
          </g>
        ))}
        {days.map((d, i) => {
          const bh = (d.count / max) * (H - 2 * pad);
          const x = pad + i * step + (step - barW) / 2;
          const y = H - pad - bh;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={bh} fill="#dab87a" rx="3" opacity={d.count > 0 ? 1 : 0.25} />
              {d.count > 0 && <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="600">{d.count}</text>}
              <text x={x + barW / 2} y={H - pad + 14} textAnchor="middle" fontSize="9" fill="currentColor" opacity="0.6">{d.date.getDate()}/{d.date.getMonth() + 1}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// --- Career path distribution donut ---
function PathDonut({ sessions, lang }: { sessions: Session[]; lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const entries = useMemo(() => {
    const map: Record<string, number> = {};
    sessions.forEach((s) => {
      const p = s.careerPath || "(none)";
      map[p] = (map[p] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [sessions]);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  const cx = 110, cy = 110, r = 80, sw = 22;
  let acc = 0;
  return (
    <div className="kd-chart-card">
      <h3 className="kd-chart-title">{t("توزيع المسارات المهنية", "Career Path Distribution")}</h3>
      <div className="kd-chart-donut-wrap">
        <svg viewBox="0 0 220 220" width="220" height="220" role="img" aria-label="Career path distribution">
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="currentColor" strokeOpacity="0.06" strokeWidth={sw} />
          {entries.map(([p, n], i) => {
            const frac = n / total;
            const dash = `${frac * 2 * Math.PI * r} ${2 * Math.PI * r}`;
            const off = -acc * 2 * Math.PI * r;
            acc += frac;
            return <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={pathColor(p)} strokeWidth={sw} strokeDasharray={dash} strokeDashoffset={off} transform={`rotate(-90 ${cx} ${cy})`} />;
          })}
          <text x={cx} y={cy - 4} textAnchor="middle" fontSize="28" fontWeight="700" fill="currentColor">{total}</text>
          <text x={cx} y={cy + 16} textAnchor="middle" fontSize="11" fill="currentColor" opacity="0.6">{t("موظف", "employees")}</text>
        </svg>
        <div className="kd-chart-legend">
          {entries.map(([p, n]) => (
            <div className="kd-chart-legend-row" key={p}>
              <span className="kd-chart-swatch" style={{ background: pathColor(p) }} />
              <span className="kd-chart-legend-name">{lang === "ar" ? PATH_LABELS_AR[p] : PATH_LABELS_EN[p]}</span>
              <span className="kd-chart-legend-num">{n} ({Math.round((n / total) * 100)}%)</span>
            </div>
          ))}
          {entries.length === 0 && <div style={{ color: "var(--kd-text-muted)", fontSize: 13 }}>{t("لا توجد بيانات بعد", "No data yet")}</div>}
        </div>
      </div>
    </div>
  );
}

// --- Conversion funnel ---
function Funnel({ sessions, lang }: { sessions: Session[]; lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const total = sessions.length;
  const steps = [
    { ar: "بدأ الرحلة", en: "Started journey", n: total, color: "#3b82f6" },
    { ar: "اختار المسار", en: "Selected path", n: sessions.filter((s) => s.careerPath).length, color: "#a855f7" },
    { ar: "أكمل الرحلة", en: "Completed journey", n: sessions.filter((s) => s.completedAt).length, color: "#10b981" },
    { ar: "قدّم تقييم", en: "Submitted rating", n: sessions.filter((s) => typeof s.rating === "number").length, color: "#dab87a" },
  ];
  const max = Math.max(1, total);
  return (
    <div className="kd-chart-card">
      <h3 className="kd-chart-title">{t("قمع التحويل", "Conversion Funnel")}</h3>
      <div className="kd-chart-funnel">
        {steps.map((s, i) => {
          const pct = (s.n / max) * 100;
          const drop = i > 0 ? steps[i - 1].n - s.n : 0;
          return (
            <div className="kd-chart-funnel-row" key={i}>
              <div className="kd-chart-funnel-label">{lang === "ar" ? s.ar : s.en}</div>
              <div className="kd-chart-funnel-bar-wrap">
                <div className="kd-chart-funnel-bar" style={{ width: `${pct}%`, background: s.color }}>
                  <span className="kd-chart-funnel-num">{s.n}</span>
                </div>
              </div>
              <div className="kd-chart-funnel-drop">
                {i === 0 ? "—" : <small style={{ color: drop > 0 ? "#ef4444" : "var(--kd-text-muted)" }}>{drop > 0 ? `−${drop}` : "0"}</small>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Interactive sessions table ---
type StatusFilter = "all" | "completed" | "in-progress" | "stale";
type RangeFilter = "7" | "30" | "90" | "all";
type SortKey = "name" | "started" | "completed" | "duration" | "rating";

function SessionsPanel({ sessions, lang, onExportAll, onClear }: { sessions: Session[]; lang: Lang; onExportAll: () => void; onClear: () => void }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [range, setRange] = useState<RangeFilter>("all");
  const [sort, setSort] = useState<SortKey>("started");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);

  const visible = useMemo(() => {
    const now = Date.now();
    const window = range === "7" ? 7 * 86400000 : range === "30" ? 30 * 86400000 : range === "90" ? 90 * 86400000 : Infinity;
    const q = query.trim().toLowerCase();
    const STALE = 3 * 86400000;
    let list = sessions.filter(
      (s) =>
        !(
          (range !== "all" && now - s.startedAt > window) ||
          (status === "completed" && !s.completedAt) ||
          (status === "in-progress" && s.completedAt) ||
          (status === "stale" && (s.completedAt || now - s.startedAt < STALE)) ||
          (q && !(s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q) || (s.careerPath || "").toLowerCase().includes(q)))
        ),
    );
    const m = dir === "asc" ? 1 : -1;
    list = list.sort((a, b) => {
      switch (sort) {
        case "name": return a.name.localeCompare(b.name) * m;
        case "completed": return ((a.completedAt || 0) - (b.completedAt || 0)) * m;
        case "duration": return ((a.durationMs || 0) - (b.durationMs || 0)) * m;
        case "rating": return ((a.rating || 0) - (b.rating || 0)) * m;
        default: return (a.startedAt - b.startedAt) * m;
      }
    });
    return list;
  }, [sessions, query, status, range, sort, dir]);

  const staleEmails = useMemo(() => new Set(visible.filter((s) => !s.completedAt && Date.now() - s.startedAt > 3 * 86400000 && s.email).map((s) => s.email)), [visible]);

  function toggleSort(key: SortKey) {
    if (sort === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(key); setDir("desc"); }
  }
  const sortMark = (key: SortKey) => (sort === key ? (dir === "asc" ? " ↑" : " ↓") : "");

  function toggleSelect(email: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  }
  function toggleSelectAllStale() {
    const allSelected = staleEmails.size > 0 && [...staleEmails].every((e) => selected.has(e));
    setSelected(allSelected ? new Set() : new Set(staleEmails));
  }

  async function sendToSelected() {
    const list = [...selected];
    if (list.length === 0 || !confirm(t(`إرسال تذكير إلى ${list.length} موظف؟`, `Send reminder to ${list.length} employee${list.length === 1 ? "" : "s"}?`))) return;
    setSending(true);
    const res: any = await sendReminders(list);
    setSending(false);
    if (!res?.ok) {
      toast.error(t("فشل الإرسال", "Send failed") + (res?.error ? `: ${res.error}` : ""));
      return;
    }
    const sent = res.results?.filter((r: any) => r.ok && !r.error).length || 0;
    const skipped = res.results?.filter((r: any) => r.error === "skipped-recent").length || 0;
    const failed = res.results?.filter((r: any) => !r.ok).length || 0;
    toast.success(t(`تم الإرسال لـ ${sent}، تخطي ${skipped} (أُرسل مؤخراً)، فشل ${failed}`, `Sent ${sent}, skipped ${skipped} (recent), failed ${failed}`));
    setSelected(new Set());
  }

  function exportVisible() {
    const headers = ["Name", "Email", "Path", "Started", "Completed", "Duration (min)", "Rating", "Comment"];
    const rows = visible.map((s) => [
      s.name, s.email, s.careerPath || "",
      new Date(s.startedAt).toISOString(),
      s.completedAt ? new Date(s.completedAt).toISOString() : "",
      s.durationMs ? Math.round(s.durationMs / 60000).toString() : "",
      typeof s.rating === "number" ? s.rating.toString() : "",
      (s.ratingComment || "").replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kaden-sessions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="kd-admin-sessions-panel">
      <div className="kd-admin-filters">
        <input type="search" placeholder={t("بحث بالاسم/البريد/المسار...", "Search by name / email / path...")} value={query} onChange={(e) => setQuery(e.target.value)} className="kd-admin-search" />
        <div className="kd-admin-chips">
          <button className={`kd-admin-chip${status === "all" ? " is-active" : ""}`} onClick={() => setStatus("all")}>{t("الكل", "All")} <span className="kd-admin-chip-count">{sessions.length}</span></button>
          <button className={`kd-admin-chip${status === "completed" ? " is-active" : ""}`} onClick={() => setStatus("completed")}>{t("مكتمل", "Completed")}</button>
          <button className={`kd-admin-chip${status === "in-progress" ? " is-active" : ""}`} onClick={() => setStatus("in-progress")}>{t("جارٍ", "In progress")}</button>
          <button className={`kd-admin-chip${status === "stale" ? " is-active" : ""}`} onClick={() => setStatus("stale")}>{t("متوقف > 3 أيام", "Stale > 3 days")}</button>
        </div>
        <div className="kd-admin-chips">
          <button className={`kd-admin-chip${range === "7" ? " is-active" : ""}`} onClick={() => setRange("7")}>7 {t("أيام", "days")}</button>
          <button className={`kd-admin-chip${range === "30" ? " is-active" : ""}`} onClick={() => setRange("30")}>30 {t("يوم", "days")}</button>
          <button className={`kd-admin-chip${range === "90" ? " is-active" : ""}`} onClick={() => setRange("90")}>90 {t("يوم", "days")}</button>
          <button className={`kd-admin-chip${range === "all" ? " is-active" : ""}`} onClick={() => setRange("all")}>{t("الكل", "All time")}</button>
        </div>
        <div className="kd-admin-toolbar" style={{ marginInlineStart: "auto" }}>
          <small style={{ color: "var(--kd-text-muted)" }}>{t("النتائج:", "Shown:")} <strong>{visible.length}</strong></small>
          <button className="kd-btn-secondary" onClick={exportVisible} disabled={visible.length === 0}>↓ {t("تصدير المعروض", "Export Visible")}</button>
          <button className="kd-btn-secondary" onClick={onExportAll} disabled={sessions.length === 0}>↓ {t("تصدير الكل", "Export All")}</button>
          <button className="kd-btn-secondary kd-btn-danger" onClick={onClear} disabled={sessions.length === 0}>{t("مسح", "Clear")}</button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="kd-admin-bulk-bar">
          <span>{t(`محدد: ${selected.size}`, `Selected: ${selected.size}`)}</span>
          <button className="kd-btn-primary kd-btn-gold" onClick={sendToSelected} disabled={sending}>
            {sending ? <span className="kd-spinner" /> : t(`إرسال تذكير للمحددين (${selected.size})`, `Send reminder to selected (${selected.size})`)}
          </button>
          <button className="kd-btn-secondary" onClick={() => setSelected(new Set())}>{t("إلغاء التحديد", "Clear selection")}</button>
        </div>
      )}

      <div className="kd-admin-table-wrap">
        <table className="kd-admin-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" title={t("تحديد المتوقفين", "Select all stale")} disabled={staleEmails.size === 0} checked={staleEmails.size > 0 && [...staleEmails].every((e) => selected.has(e))} onChange={toggleSelectAllStale} />
              </th>
              <th className="kd-sortable" onClick={() => toggleSort("name")}>{t("الموظف", "Employee")}{sortMark("name")}</th>
              <th>{t("البريد", "Email")}</th>
              <th>{t("المسار", "Path")}</th>
              <th className="kd-sortable" onClick={() => toggleSort("started")}>{t("بدأ في", "Started")}{sortMark("started")}</th>
              <th className="kd-sortable" onClick={() => toggleSort("completed")}>{t("انتهى في", "Completed")}{sortMark("completed")}</th>
              <th>{t("المدة", "Duration")}</th>
              <th>{t("التقييم", "Rating")}</th>
              <th>{t("الحالة", "Status")}</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: "center", padding: "2rem", color: "var(--kd-text-muted)" }}>{sessions.length === 0 ? t("لا توجد بيانات حتى الآن.", "No data yet.") : t("لا توجد نتائج مطابقة للفلتر.", "No sessions match the current filter.")}</td></tr>
            )}
            {visible.map((s, i) => {
              const stale = !s.completedAt && Date.now() - s.startedAt > 259200000;
              return (
                <tr key={i} className={stale ? "kd-admin-row-stale" : ""}>
                  <td><input type="checkbox" disabled={!s.email || !stale} title={stale ? "" : t("المتاحة فقط للمتوقفين > 3 أيام", "Only available for stale > 3 days")} checked={selected.has(s.email)} onChange={() => toggleSelect(s.email)} /></td>
                  <td>{s.name || "—"}</td>
                  <td><small style={{ fontFamily: "var(--kd-font-en)" }}>{s.email || "—"}</small></td>
                  <td>{s.careerPath ? <span className="kd-path-badge" style={{ background: `${pathColor(s.careerPath)}22`, color: pathColor(s.careerPath), borderColor: `${pathColor(s.careerPath)}55` }}>{s.careerPath}</span> : "—"}</td>
                  <td><small>{formatDateTime(s.startedAt, lang)}</small></td>
                  <td><small>{s.completedAt ? formatDateTime(s.completedAt, lang) : "—"}</small></td>
                  <td>{formatDuration(s.durationMs, lang)}</td>
                  <td>
                    {typeof s.rating === "number" ? (
                      <div>
                        <div>{"★".repeat(s.rating)}<span style={{ opacity: 0.3 }}>{"★".repeat(5 - s.rating)}</span></div>
                        {s.ratingComment && <small style={{ display: "block", color: "var(--kd-text-muted)", marginTop: 4, whiteSpace: "pre-wrap", maxWidth: 260 }}>{s.ratingComment}</small>}
                      </div>
                    ) : "—"}
                  </td>
                  <td>
                    {s.completedAt ? <span className="kd-admin-badge kd-admin-badge-ok">{t("مكتمل", "Done")}</span>
                      : stale ? <span className="kd-admin-badge kd-admin-badge-stale">{t("متوقف", "Stale")}</span>
                      : <span className="kd-admin-badge kd-admin-badge-pending">{t("قيد التقدّم", "In progress")}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Analytics & Reports tab (lt) — stats, three charts, and an interactive sessions table. */
export function AnalyticsTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [store, setStore] = useState(() => getAnalyticsStore());
  const [remote, setRemote] = useState<Session[] | null>(null);

  useEffect(() => subscribeAnalytics(() => setStore(getAnalyticsStore())), []);
  useEffect(() => {
    getSessions().then((s) => s && setRemote(s as Session[]));
  }, []);

  const merged = useMemo(() => {
    const map = new Map<string, Session>();
    for (const s of store.sessions) map.set(s.email, s);
    if (remote) for (const s of remote) map.set(s.email, s);
    return [...map.values()].sort((a, b) => b.startedAt - a.startedAt);
  }, [store, remote]);

  const completed = merged.filter((s) => s.completedAt);
  const rated = merged.filter((s) => typeof s.rating === "number");
  const avgRating = rated.length > 0 ? (rated.reduce((a, s) => a + (s.rating || 0), 0) / rated.length).toFixed(1) : "—";
  const avgDuration = completed.length > 0 ? completed.reduce((a, s) => a + (s.durationMs || 0), 0) / completed.length : 0;
  const completionRate = merged.length > 0 ? Math.round((completed.length / merged.length) * 100) : 0;

  function exportAll() {
    const csv = exportAnalyticsCSV();
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kaden-onboarding-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    logAudit("config_exported", "Analytics CSV exported");
  }
  function clearAll() {
    if (confirm(t("سيتم حذف جميع البيانات التحليلية. متأكد؟", "All analytics data will be deleted. Are you sure?"))) {
      clearAnalytics();
      logAudit("analytics_cleared", "All analytics data cleared");
    }
  }

  return (
    <div className="kd-admin-analytics">
      <div className="kd-admin-stats">
        <div className="kd-admin-stat"><div className="kd-admin-stat-num">{merged.length}</div><div className="kd-admin-stat-label">{t("إجمالي الموظفين", "Total Employees")}</div></div>
        <div className="kd-admin-stat"><div className="kd-admin-stat-num">{completed.length}</div><div className="kd-admin-stat-label">{t("أكملوا الرحلة", "Completed")}</div></div>
        <div className="kd-admin-stat"><div className="kd-admin-stat-num">{completionRate}%</div><div className="kd-admin-stat-label">{t("نسبة الإكمال", "Completion rate")}</div></div>
        <div className="kd-admin-stat"><div className="kd-admin-stat-num">{avgRating}</div><div className="kd-admin-stat-label">{t("متوسط التقييم", "Avg. Rating")} ★</div></div>
        <div className="kd-admin-stat"><div className="kd-admin-stat-num">{formatDuration(avgDuration, lang)}</div><div className="kd-admin-stat-label">{t("متوسط المدة", "Avg. Duration")}</div></div>
      </div>
      <div className="kd-charts-grid">
        <CompletionsChart sessions={store.sessions} lang={lang} />
        <PathDonut sessions={store.sessions} lang={lang} />
        <Funnel sessions={store.sessions} lang={lang} />
      </div>
      <SessionsPanel sessions={merged} lang={lang} onExportAll={exportAll} onClear={clearAll} />
    </div>
  );
}
